import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { sha256 } from 'js-sha256';

// ~~~~~~~~~~~~~~~~~~~ Enable ~~~~~~~~~~~~~~~~~~~
const metricsEnabled = process.env.ENABLE_METRICS === 'true';
const site = process.env.METRICS_SITE || '';

// // ~~~~~~~~~~~~~~~~~~~ Interface / Types ~~~~~~~~~~~~~~~~~~~
// Event enum
export enum InsightType {
  Login = 'LoginEvent',
  PageLoad = 'PageLoadEvent',
  PageViewPing = 'PageViewPing',
  Click = 'ClickEvent',
  Video = 'VideoEvent',
  Search = 'SearchEvent',
}

// Shared base interface
interface BaseEvent {
  userId: string;
  sessionId: string;
  timestamp: string;
  type: InsightType;
  site: string;
}

// 1. Login Event
export interface LoginEvent extends BaseEvent {
  userRole: string;
  location?: string;
  referrer?: string; // original referrer captured at session start
  // Origin metadata (captured once when session starts)
  originReferrerDomain?: string;
  originLandingUrl?: string;
  originSourceType?: string; // 'campaign' | 'external' | 'internal' | 'direct'
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

// 2. Page Load Event
export interface PageLoadEvent extends BaseEvent {
  contentfulPageId: string;
  pageTitle: string;
  pageUrl: string;
  referrer?: string;
}

// 3. Page View Ping Event
export interface PageViewPing extends BaseEvent {
  contentfulPageId: string;
  pageTitle: string;
  pageUrl: string;
  duration: number; // in seconds
}

// 4. Click Event
export interface ClickEvent extends BaseEvent {
  contentfulComponentId?: string;
  linkTitle?: string;
  elementType: 'button' | 'link' | 'icon' | 'custom';
  elementId?: string;
  pageUrl: string;
  linkUrl?: string;
}

// 5. Video Event
export interface VideoEvent extends BaseEvent {
  contentfulComponentId: string;
  videoURL: string;
  action: 'start' | 'pause' | 'resume' | 'finish' | 'progress25' | 'progress50' | 'progress75';
  playbackPosition: number;
  videoLength: number;
  contentfulPageId: string;
  pageTitle: string;
  pageUrl: string;
  title: string; // human-readable title for the video
  caption?: string; // human-readable caption/description for the video (always send empty string if not available)
}

type SearchResult = {
  description: string;
  url: string;
  type: string;
  title: string;
};

//6. Search Event
export interface SearchEvent extends BaseEvent {
  searchTerm: string;
  searchResults: SearchResult[];
  searchResultCount: number;
}

type InsightEvent =
  | LoginEvent
  | PageLoadEvent
  | PageViewPing
  | ClickEvent
  | VideoEvent
  | SearchEvent;

// ~~~~~~~~~~~~~~~~~~~ State Setup ~~~~~~~~~~~~~~~~~~~

interface UserInsightState {
  userId: string;
  sessionId: string;
}

interface CurPageState {
  contentfulPageId: string;
  pageTitle: string;
  pageUrl: string;
}

declare global {
  interface Window {
    __AOP_METRICS_INIT__?: boolean;
    __AOP_VIDEO_QUEUE_INTERVAL_ID__?: number;
    __AOP_PAGEHIDE_FIRED__?: boolean;
    __AOP_VISIBILITY_TIMEOUT__?: NodeJS.Timeout;
  }
}

// ~~~~~~~~~~~~~~~~~~~ Functional Setup ~~~~~~~~~~~~~~~~~~~

// Initialize Azure App Insights
let appInsights: ApplicationInsights;
if (metricsEnabled) {
  appInsights = new ApplicationInsights({
    config: {
      instrumentationKey: process.env.INSIGHTS_INSTRUMENTATION,
      // Enable sendBeacon API for mobile reliability
      // Ensures events sent during page unload/backgrounding are not cancelled
      disableFetchTracking: false,
      // Force use of sendBeacon for page unload events (mobile-critical)
      onunloadDisableBeacon: false,
    },
  });
  appInsights.loadAppInsights();
}

// Track if appInsights is ready
let appInsightsReady = false;
// Give appInsights a moment to initialize
if (typeof window !== 'undefined') {
  // Check after next tick to ensure SDK is ready
  setTimeout(() => {
    appInsightsReady = true;
  }, 100);
}

// Mutable in-memory state
const userInsightState: UserInsightState = {
  userId: '',
  sessionId: '',
};
const curPage: CurPageState = {
  contentfulPageId: '',
  pageTitle: '',
  pageUrl: '',
};

export const getUserInsightState = () => userInsightState;

export const initUserInsight = (profile: {
  id: string;
  officeLocation?: string;
}) => {
  if (!metricsEnabled) return;
  if (!profile?.id) return;

  const stored = sessionStorage.getItem('userInsightInfo');
  if (stored) {
    const parsed = JSON.parse(stored);
    userInsightState.userId = parsed.userId;
    userInsightState.sessionId = parsed.sessionId;
    return;
  }

  const loginTime = new Date().getTime();
  const userId = profile.id;
  const sessionId = sha256(profile.id + loginTime);

  userInsightState.userId = userId;
  userInsightState.sessionId = sessionId;

  // Capture origin details once per session
  let referrer = '';
  let originReferrerDomain = '';
  let originLandingUrl = '';
  let originSourceType = '';
  let utmSource = '';
  let utmMedium = '';
  let utmCampaign = '';

  try {
    if (typeof window !== 'undefined') {
      referrer = typeof document !== 'undefined' ? (document.referrer || '') : '';
      originLandingUrl = window.location.href;
      const params = new URLSearchParams(window.location.search);
      utmSource = params.get('utm_source') || '';
      utmMedium = params.get('utm_medium') || '';
      utmCampaign = params.get('utm_campaign') || '';
      try {
        if (referrer) {
          const refUrl = new URL(referrer);
          originReferrerDomain = refUrl.host;
        }
      } catch {
        // ignore parse errors
      }
      const hasUtm = !!(utmSource || utmMedium || utmCampaign);
      const currentHost = window.location.host;
      if (hasUtm) originSourceType = 'campaign';
      else if (!referrer) originSourceType = 'direct';
      else if (originReferrerDomain === currentHost) originSourceType = 'internal';
      else originSourceType = 'external';
    }
  } catch {
    // swallow any unexpected errors
  }

  sessionStorage.setItem(
    'userInsightInfo',
    JSON.stringify({
      userId,
      sessionId,
      originReferrerDomain,
      originLandingUrl,
      originSourceType,
      utmSource,
      utmMedium,
      utmCampaign,
    }),
  );

  // send login event (referrer now represents original referrer captured above)
  const loginEvent: LoginEvent = {
    userId,
    sessionId,
    userRole: '', // optional
    location: profile.officeLocation ?? '',
    timestamp: loginTime.toString(),
    referrer: referrer,
    originReferrerDomain,
    originLandingUrl,
    originSourceType,
    utmSource,
    utmMedium,
    utmCampaign,
    type: InsightType.Login,
    site: site,
  };

  // console.log('Sending login event:');
  // console.log(JSON.stringify(loginEvent));
  appInsights.trackEvent({ name: InsightType.Login, properties: loginEvent });

  // Mark appInsights as ready after first successful event
  appInsightsReady = true;
};

export const sendInsight = (insight: InsightEvent, retry = 0): void => {
  if (!metricsEnabled) return;
  
  // Check if appInsights is ready (for first events after page load)
  if (!appInsightsReady) {
    if (retry < 5) {
      setTimeout(() => sendInsight(insight, retry + 1), 200);
    } else {
      console.warn('Application Insights not ready after retries. Skipping event:', insight.type);
    }
    return;
  }
  
  if (userInsightState.userId === '' || userInsightState.sessionId === '') {
    const stored = sessionStorage.getItem('userInsightInfo');
    if (stored) {
      const parsed = JSON.parse(stored);
      userInsightState.userId = parsed.userId;
      userInsightState.sessionId = parsed.sessionId;
    }
  }
  const { userId, sessionId } = userInsightState;

  if (!userId || !sessionId) {
    if (retry < 5) {
      setTimeout(() => sendInsight(insight, retry + 1), 200);
    } else {
      console.warn('User ID or Session ID not ready after retries. Skipping event:', insight.type);
    }
    return;
  }

  const event = {
    ...insight,
    userId,
    sessionId,
    site: site,
  };

  if (insight.type === InsightType.PageLoad) {
    // Determine referrer as the previous page URL stored in session, fallback to document.referrer
    let priorUrl = '';
    try {
      const storedPrev = sessionStorage.getItem('curPageInfo');
      if (storedPrev) {
        const parsedPrev = JSON.parse(storedPrev);
        priorUrl = parsedPrev?.pageUrl || '';
      } else if (typeof document !== 'undefined') {
        priorUrl = document.referrer || '';
      }
    } catch {
      // ignore errors reading/parsing storage
    }

    const pageTitle = (insight as PageLoadEvent).pageTitle;
    const pageUrl = (insight as PageLoadEvent).pageUrl;
    const pageEvent: PageLoadEvent = {
      ...(event as PageLoadEvent),
      referrer: priorUrl,
    };

    // console.log('Sending page view event:');
    // console.log(
    //   JSON.stringify({
    //     name: pageTitle,
    //     uri: pageUrl,
    //     properties: pageEvent,
    //   }),
    // );
    appInsights.trackPageView({
      name: pageTitle,
      uri: pageUrl,
      properties: pageEvent,
    });

    // Update current page info after sending so it becomes the referrer for the next page
    curPage.contentfulPageId = (insight as PageLoadEvent).contentfulPageId;
    curPage.pageTitle = pageTitle;
    curPage.pageUrl = pageUrl;
    sessionStorage.setItem('curPageInfo', JSON.stringify(curPage));
  } else {
    // If Search event, trim large payloads: cap searchResults at top 5, keep full count
    if (insight.type === InsightType.Search) {
      const src = insight as SearchEvent;
      const trimmedSearchEvent: SearchEvent = {
        ...src,
        searchResults: Array.isArray(src.searchResults)
          ? src.searchResults.slice(0, 5)
          : [],
        searchResultCount: typeof src.searchResultCount === 'number'
          ? src.searchResultCount
          : (Array.isArray(src.searchResults) ? src.searchResults.length : 0),
      };

      const eventWithIds = {
        ...trimmedSearchEvent,
        userId,
        sessionId,
        site: site,
      };

      // console.log('Sending general insight:');
      // console.log(JSON.stringify({ name: insight.type, properties: eventWithIds }));
      appInsights.trackEvent({ name: insight.type, properties: eventWithIds });
      return;
    }
    // console.log('Sending general insight:');
    // console.log(JSON.stringify({ name: insight.type, properties: event }));
    appInsights.trackEvent({ name: insight.type, properties: event });
  }
};

// Track time spent on each page
let pageStartTime: number | null = null;
// Video event batching queue
const videoEventQueue: VideoEvent[] = [];
const FLUSH_INTERVAL_MS = 5000;
const QUEUE_MAX_BEFORE_FLUSH = 6;
// Video event retry queue (for when curPage is not ready yet)
const pendingVideoEvents: VideoEvent[] = [];
let videoRetryTimeoutId: NodeJS.Timeout | null = null;

const flushVideoQueue = () => {
  if (!metricsEnabled) return;
  if (!videoEventQueue.length) return;
  // Flush queued (non-critical) video events
  const toSend = videoEventQueue.splice(0, videoEventQueue.length);
  toSend.forEach(ev => sendInsight(ev));
};

// Initialize one-time listeners / intervals only once
if (typeof window !== 'undefined' && metricsEnabled && !window.__AOP_METRICS_INIT__) {
  window.__AOP_METRICS_INIT__ = true;
  
  // Primary event: pagehide (desktop browsers)
  window.addEventListener('pagehide', () => {
    window.__AOP_PAGEHIDE_FIRED__ = true;
    trackPageTime();
    flushVideoQueue();
  });
  
  // Mobile-friendly fallback: visibilitychange (iOS Safari, Android Chrome)
  // Only fire if page is actually being discarded, not just backgrounded
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // Check if pagehide was already called (avoid duplicate events)
        if (window.__AOP_PAGEHIDE_FIRED__) return;
        
        // Use a timeout to detect if this is a tab switch vs actual unload
        // If the page becomes visible again within 300ms, it was just a tab switch
        const timeoutId = setTimeout(() => {
          trackPageTime();
          flushVideoQueue();
        }, 300);
        
        // Store timeout ID so we can cancel it if page becomes visible again
        window.__AOP_VISIBILITY_TIMEOUT__ = timeoutId;
      } else if (document.visibilityState === 'visible') {
        // Page came back into focus - cancel pending PageViewPing
        if (window.__AOP_VISIBILITY_TIMEOUT__) {
          clearTimeout(window.__AOP_VISIBILITY_TIMEOUT__);
          window.__AOP_VISIBILITY_TIMEOUT__ = undefined;
        }
      }
    });
  }
  
  // Secondary fallback: beforeunload (older browsers)
  window.addEventListener('beforeunload', () => {
    trackPageTime();
    flushVideoQueue();
  });
  
  pageStartTime = Date.now();
  if (!window.__AOP_VIDEO_QUEUE_INTERVAL_ID__) {
    window.__AOP_VIDEO_QUEUE_INTERVAL_ID__ = window.setInterval(flushVideoQueue, FLUSH_INTERVAL_MS);
  }
}

// tracks the time spent on the page
export const trackPageTime = () => {
  if (!metricsEnabled) return;

  // Ensure curPage is populated
  if (curPage.contentfulPageId === '') {
    const stored = sessionStorage.getItem('curPageInfo');
    if (stored) {
      const parsed = JSON.parse(stored);
      curPage.contentfulPageId = parsed.contentfulPageId;
      curPage.pageTitle = parsed.pageTitle;
      curPage.pageUrl = parsed.pageUrl;
    }
  }

  // Calculate duration
  if (pageStartTime && curPage.contentfulPageId !== '') {
    const duration = Math.floor((Date.now() - pageStartTime) / 1000);
    if (duration < 1) return; // Ignore very short durations

    const pageViewPing: PageViewPing = {
      userId: userInsightState.userId,
      sessionId: userInsightState.sessionId,
      timestamp: new Date().getTime().toString(),
      type: InsightType.PageViewPing,
      contentfulPageId: curPage.contentfulPageId,
      pageTitle: curPage.pageTitle,
      pageUrl: curPage.pageUrl,
      duration,
      site: site,
    };

    sendInsight(pageViewPing);
  }
  pageStartTime = Date.now(); // Reset start time for the next page
};

// Note: Event listeners are now initialized in the block above (lines 355-380)
// to avoid duplicate listener registration. This comment preserves the intent
// of the original code for reference.

export const sendVideoInsight = (insight: VideoEvent) => {
  if (!metricsEnabled) return;
  
  // Ensure caption is always a string (never null/undefined)
  const insightWithCaption = {
    ...insight,
    caption: insight.caption || '',
  };
  
  // Try to populate curPage from sessionStorage
  if (curPage.contentfulPageId === '') {
    const stored = sessionStorage.getItem('curPageInfo');
    if (stored) {
      const parsed = JSON.parse(stored);
      curPage.contentfulPageId = parsed.contentfulPageId;
      curPage.pageTitle = parsed.pageTitle;
      curPage.pageUrl = parsed.pageUrl;
    }
  }
  
  // If curPage still not ready, queue for retry (handles first page load race condition)
  if (curPage.contentfulPageId === '') {
    pendingVideoEvents.push(insightWithCaption);
    
    // Set up retry if not already scheduled
    if (!videoRetryTimeoutId) {
      videoRetryTimeoutId = setTimeout(() => {
        videoRetryTimeoutId = null;
        // Retry all pending events
        const eventsToRetry = [...pendingVideoEvents];
        pendingVideoEvents.length = 0;
        eventsToRetry.forEach(event => sendVideoInsight(event));
      }, 300); // Wait 300ms for PageLoadEvent to complete
    }
    return; // Don't send yet
  }
  
  // curPage is ready - send the event
  const event: VideoEvent = {
    ...insightWithCaption,
    contentfulPageId: curPage.contentfulPageId,
    pageTitle: curPage.pageTitle,
    pageUrl: curPage.pageUrl,
  };
  
  // Critical actions (start/finish) send immediately; others are batched
  if (event.action === 'start' || event.action === 'finish') {
    sendInsight(event);
    return;
  }
  videoEventQueue.push(event);
  if (videoEventQueue.length >= QUEUE_MAX_BEFORE_FLUSH) {
    flushVideoQueue();
  }
};

// Global window augmentation for guards
declare global {
  interface Window {
    __AOP_METRICS_INIT__?: boolean;
    __AOP_VIDEO_QUEUE_INTERVAL_ID__?: number;
  }
}
